import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, Dimensions, Platform, Animated, Easing } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import LogoSvg from '../LogoSvg';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

  const LoadingScreen = ({ onFinish }) => {
  const [darkMode, setDarkMode] = useState(false);
  const themeT = useRef(new Animated.Value(0)).current; // 0 white, 1 black
  const [hidden, setHidden] = useState(false);
  const [phase, setPhase] = useState('loading'); // 'loading' | 'align' | 'curtain'
  const rowsPendingRef = useRef(0);
  const FINISH_MS = 1100;
  const AFTER_ALIGN_DELAY_MS = 150;

  const logoSize = Math.max(72, screenWidth * 0.14);
  const fontSize = 300;

  const rows = useMemo(() => (
    [0, 1, 2, 3].map((idx) => ({ id: idx, direction: idx % 2 === 0 ? 'left' : 'right' }))
  ), []);

  const curtainAnim = useRef(new Animated.Value(0)).current; // 0 shown, 1 hidden (curtain up)

  useEffect(() => {
    const themeTimer = setTimeout(() => {
      Animated.timing(themeT, {
        toValue: 1,
        duration: 900,
        easing: Easing.inOut(Easing.cubic),
        useNativeDriver: false,
      }).start(() => setDarkMode(true));
    }, 1400);
    const finishTimer = setTimeout(() => {
      // trigger align phase
      rowsPendingRef.current = 4;
      setPhase('align');
    }, 2300);

    return () => {
      clearTimeout(themeTimer);
      clearTimeout(finishTimer);
    };
  }, []);

  const openCurtain = () => {
    if (phase === 'curtain') return;
    setPhase('curtain');
    Animated.timing(curtainAnim, {
      toValue: 1,
      duration: 900,
      easing: Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: false,
    }).start(() => {
      setHidden(true);
      onFinish && onFinish();
    });
  };

  const translateY = curtainAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -(screenHeight + 600)], // slide well beyond viewport
  });

  const bgColor = themeT.interpolate({ inputRange: [0, 1], outputRange: ['#FFFFFF', '#000000'] });
  const textColor = themeT.interpolate({ inputRange: [0, 1], outputRange: ['#000000', '#FFFFFF'] });
  const AnimatedText = Animated.createAnimatedComponent(Text);

  const MarqueeRow = ({ direction, speedPxPerSec = 220, index = 0, onAligned }) => {
    const translate = useRef(new Animated.Value(0)).current;
    const segmentWidthRef = useRef(0);
    const measuredRef = useRef(false);
    const loopRef = useRef(null);
    const rowHeight = Math.max(fontSize, logoSize) * 1.05;
    const sidePadding = 0;
    const initialOffsetRef = useRef(null);
    const runningRef = useRef(false);
    const currentXRef = useRef(0);

    // Track live translate value for precise alignment start
    useEffect(() => {
      const id = translate.addListener(({ value }) => {
        currentXRef.current = value;
      });
      return () => translate.removeListener(id);
    }, [translate]);
    

    const startLoop = () => {
      if (phase !== 'loading') return;
      runningRef.current = true;
      const distance = (segmentWidthRef.current || screenWidth);
      const duration = Math.max(1200, Math.round((Math.abs(distance) / speedPxPerSec) * 1000));

      // Choose a randomized starting offset once per row
      // Deterministic wide offsets per row so they start visibly misaligned
      const fractions = [0.05, 0.33, 0.62, 0.85];
      const fraction = fractions[index % fractions.length];
      const baseOffsetCandidate = -fraction * distance;
      const baseOffset = initialOffsetRef.current !== null ? initialOffsetRef.current : baseOffsetCandidate;
      if (initialOffsetRef.current === null) initialOffsetRef.current = baseOffset;

      // For leftward rows animate negatively; rightward positively
      const start = baseOffset;
      const end = direction === 'left' ? baseOffset - distance : baseOffset + distance;

      if (loopRef.current) loopRef.current();

      const run = () => {
        if (!runningRef.current || phase !== 'loading') return;
        translate.setValue(start);
        Animated.timing(translate, {
          toValue: end,
          duration,
          easing: Easing.linear,
          useNativeDriver: false,
        }).start(({ finished }) => {
          if (finished && runningRef.current && phase === 'loading') run();
        });
      };
      loopRef.current = run;
      run();
    };

    useEffect(() => {
      // Kick off an initial loop using screen width; refine after layout
      startLoop();
      return () => {
        runningRef.current = false;
        loopRef.current = null;
        translate.stopAnimation();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [direction, phase]);

    // Align when parent switches to 'align'
    useEffect(() => {
      if (phase !== 'align' || measuredRef.current === false) return;
      runningRef.current = false;
      const distance = (segmentWidthRef.current || screenWidth);
      const beginAlign = (val) => {
        let target = direction === 'left'
          ? Math.ceil(val / distance) * distance // reverse: move right
          : Math.floor(val / distance) * distance; // reverse: move left
        // Ensure a noticeable travel; if too small, extend one more segment
        const minShift = distance * 0.35;
        if (Math.abs(target - val) < minShift) {
          target += direction === 'left' ? distance : -distance;
        }
        Animated.timing(translate, {
          toValue: target,
          duration: FINISH_MS,
          easing: Easing.bezier(0.22, 1, 0.36, 1),
          useNativeDriver: false,
        }).start(() => {
          onAligned && onAligned();
        });
      };
      // Stop current animation and animate from exact current value
      translate.stopAnimation((val) => {
        const current = typeof val === 'number' ? val : currentXRef.current;
        beginAlign(current);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    

    const onLayoutSegment = (e) => {
      if (measuredRef.current) return; // prevent restart on color flip re-renders
      segmentWidthRef.current = e.nativeEvent.layout.width;
      measuredRef.current = true;
      if (phase === 'loading') startLoop();
      if (phase === 'align') {
        // if phase already switched before measure, trigger align once
        const distance = (segmentWidthRef.current || screenWidth);
        translate.stopAnimation((val) => {
          const current = typeof val === 'number' ? val : currentXRef.current;
          let target = direction === 'left'
            ? Math.ceil(current / distance) * distance
            : Math.floor(current / distance) * distance;
          const minShift = distance * 0.35;
          if (Math.abs(target - current) < minShift) {
            target += direction === 'left' ? distance : -distance;
          }
          Animated.timing(translate, {
            toValue: target,
            duration: FINISH_MS,
            easing: Easing.bezier(0.22, 1, 0.36, 1),
            useNativeDriver: false,
          }).start(() => {
            onAligned && onAligned();
          });
        });
      }
    };

    const TextUnit = () => (
      <AnimatedText style={[styles.brandText, { fontSize, lineHeight: fontSize, color: textColor }]}>BETOPTIMIZED</AnimatedText>
    );
    const LogoUnit = () => (
      <View style={[styles.logoContainer, { width: logoSize, height: logoSize }]}>
        <LogoSvg width={logoSize * 0.8} height={logoSize * 0.8} spin color={'rgba(0,0,0,0.8)'} />
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: themeT }]}> 
          <LogoSvg width={logoSize * 0.8} height={logoSize * 0.8} spin color={'rgba(255,255,255,0.85)'} />
        </Animated.View>
      </View>
    );

    const Pattern = () => (
      <View style={styles.patternRow}>
        {/* Repeat [Text, Logo] only, so seams are Logo|Text (no double logos) */}
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
        <TextUnit /><LogoUnit />
    </View>
  );

  return (
      <View style={[styles.rowMask, { height: rowHeight, paddingLeft: sidePadding, paddingRight: sidePadding }]}>
        <Animated.View style={[styles.track, { transform: [{ translateX: translate }] }]}> 
          {/* Segment A (measured) */}
          <View onLayout={onLayoutSegment} style={styles.segment}>
            <Pattern />
          </View>
          {/* Segment B (duplicate) */}
          <View style={styles.segment}>
            <Pattern />
          </View>
          {/* Segment C (duplicate) ensures fill on both edges */}
          <View style={styles.segment}>
            <Pattern />
          </View>
        </Animated.View>
      </View>
    );
  };

  if (hidden) return null;

  return (
    <Animated.View style={[styles.container, { backgroundColor: bgColor, transform: [{ translateY }] }]}>
      <StatusBar style={darkMode ? 'light' : 'dark'} backgroundColor={darkMode ? '#000' : '#FFF'} />
      <View style={styles.content}>
        {rows.map((r) => (
          <MarqueeRow
            key={r.id}
            index={r.id}
            direction={r.direction}
            onAligned={() => {
              rowsPendingRef.current -= 1;
              if (rowsPendingRef.current <= 0) {
                setTimeout(() => openCurtain(), AFTER_ALIGN_DELAY_MS);
              }
            }}
          />
        ))}
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderWidth: 2,
    borderColor: '#000000',
    ...(Platform.OS === 'web' 
      ? { boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }
      : {
          shadowColor: '#000000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 4,
        }
    ),
    backgroundColor: '#000',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'stretch',
    paddingHorizontal: 0,
    gap: 16,
  },
  rowMask: {
    width: '100%',
    overflow: 'hidden',
    alignItems: 'center',
  },
  track: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  segment: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  patternRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: screenWidth * 0.02,
  },
  unit: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: screenWidth * 0.02,
  },
  brandText: {
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: -1.2,
    ...(Platform.OS === 'web' 
      ? { fontFamily: 'ClimateCrisis' }
      : { fontFamily: 'ClimateCrisis' }),
  },
  logoContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
  },
});

export default LoadingScreen;
